import clsx from 'clsx';
import React from 'react';

import claudeLogo from './logos/claude.svg';
import codexLogo from './logos/codex.svg';
import cursorLogo from './logos/cursor.svg';
import cursorDarkLogo from './logos/cursor-dark.svg';
import opencodeLogo from './logos/opencode.svg';
import opencodeDarkLogo from './logos/opencode-dark.svg';

export type McpHost = 'claude' | 'cursor' | 'codex' | 'opencode';

export type McpHostDefinition = {
  host: McpHost;
  label: string;
  // Matched case-insensitively against the OAuth-registered client name
  // (mcpClient.name), e.g. "Claude Code", "Cursor".
  pattern: RegExp;
};

// `dark` falls back to `light` for hosts whose mark works in both color modes.
const HOST_LOGOS: Record<McpHost, { light: string; dark: string }> = {
  claude: { light: claudeLogo, dark: claudeLogo },
  cursor: { light: cursorLogo, dark: cursorDarkLogo },
  codex: { light: codexLogo, dark: codexLogo },
  opencode: { light: opencodeLogo, dark: opencodeDarkLogo },
};

const HOST_DEFINITIONS: readonly McpHostDefinition[] = [
  { host: 'claude', label: 'Claude', pattern: /claude/i },
  { host: 'cursor', label: 'Cursor', pattern: /cursor/i },
  { host: 'codex', label: 'Codex', pattern: /codex/i },
  { host: 'opencode', label: 'OpenCode', pattern: /open\s?code/i },
];

export const detectMcpHost = (
  clientName: string | null | undefined
): McpHostDefinition | undefined =>
  clientName
    ? HOST_DEFINITIONS.find((def) => def.pattern.test(clientName))
    : undefined;

export const McpHostLogo = ({
  name,
  size = 20,
  className,
}: {
  name: string | null | undefined;
  size?: number;
  className?: string;
}): JSX.Element => {
  const host = detectMcpHost(name);
  // When a className controls sizing, skip the inline size box.
  const sizeStyle = className ? undefined : { width: size, height: size };

  if (host) {
    const logos = HOST_LOGOS[host.host];
    const hasDarkVariant = logos.dark !== logos.light;
    return (
      <span
        className={clsx(
          'inline-flex items-center justify-center shrink-0 overflow-hidden border-[0.5px] border-main-300 bg-negative',
          className
        )}
        style={sizeStyle}
        title={host.label}
      >
        <img
          src={logos.light}
          alt={host.label}
          className={clsx(
            'w-[70%] h-[70%] object-contain',
            hasDarkVariant && 'dark:hidden'
          )}
        />
        {hasDarkVariant && (
          <img
            src={logos.dark}
            alt=""
            aria-hidden
            className="w-[70%] h-[70%] object-contain hidden dark:block"
          />
        )}
      </span>
    );
  }

  const initial = (name?.trim().charAt(0) || '?').toUpperCase();
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center shrink-0 bg-main-200 text-main-500 text-xs font-medium uppercase',
        className ?? 'rounded-full'
      )}
      style={sizeStyle}
      aria-hidden
    >
      {initial}
    </span>
  );
};
